import { jsPDF } from "jspdf";

const MARGIN = 18;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const LINE_HEIGHT = 5.5;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/sthkmul7_SDORA_Logo-copy.png";

// Cache the logo as base64
let _logoBase64 = null;

async function loadLogo() {
  if (_logoBase64) return _logoBase64;
  try {
    const resp = await fetch(LOGO_URL);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => { _logoBase64 = reader.result; resolve(_logoBase64); };
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ── Shared PDF helpers factory ──
function createPdfHelpers(doc) {
  let y = MARGIN;

  const checkPage = (needed = 18) => {
    if (y + needed > 275) { doc.addPage(); y = MARGIN + 5; }
  };

  const addSectionHeader = (text) => {
    checkPage(14);
    doc.setFillColor(30, 58, 95); // navy
    doc.rect(MARGIN, y, CONTENT_WIDTH, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255);
    doc.text(text, MARGIN + 3, y + 5);
    doc.setTextColor(0);
    y += 10;
  };

  const addField = (label, value) => {
    if (!value && value !== 0) return;
    checkPage(8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(100);
    doc.text(label, MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(30);
    const labelW = doc.getTextWidth(label + "  ");
    const maxW = CONTENT_WIDTH - labelW - 2;
    const lines = doc.splitTextToSize(String(value), maxW);
    doc.text(lines, MARGIN + labelW, y);
    y += Math.max(LINE_HEIGHT, lines.length * 4.2);
    doc.setTextColor(0);
  };

  const addFieldRow = (pairs) => {
    checkPage(8);
    const colW = CONTENT_WIDTH / pairs.length;
    pairs.forEach(([label, value], i) => {
      if (!value && value !== 0) return;
      const x = MARGIN + i * colW;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(100);
      doc.text(label, x, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(30);
      const labelW = doc.getTextWidth(label + "  ");
      const valText = String(value).substring(0, 60);
      doc.text(valText, x + labelW, y);
      doc.setTextColor(0);
    });
    y += LINE_HEIGHT;
  };

  const addLongText = (label, value) => {
    if (!value) return;
    checkPage(12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(100);
    doc.text(label, MARGIN, y);
    y += 3.5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(30);
    const lines = doc.splitTextToSize(String(value), CONTENT_WIDTH - 4);
    lines.forEach((line) => { checkPage(5); doc.text(line, MARGIN + 2, y); y += 3.8; });
    y += 2;
    doc.setTextColor(0);
  };

  const addNotes = (notes) => {
    if (!notes || notes.length === 0) return;
    addSectionHeader(`Case Notes (${notes.length})`);
    notes.forEach((n) => {
      checkPage(14);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(100);
      const ts = n.created_at ? new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";
      doc.text(`${n.author || "Admin"} — ${ts}`, MARGIN, y);
      y += 3.5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(30);
      const lines = doc.splitTextToSize(n.text || "", CONTENT_WIDTH - 4);
      lines.forEach((line) => { checkPage(5); doc.text(line, MARGIN + 2, y); y += 3.8; });
      y += 2;
      doc.setTextColor(0);
    });
  };

  const spacer = (h = 3) => { y += h; };

  return { checkPage, addSectionHeader, addField, addFieldRow, addLongText, addNotes, spacer, getY: () => y, setY: (v) => { y = v; } };
}

// ── Page 1 Letterhead ──
function addLetterhead(doc, logo, reportTitle, subtitle) {
  // Navy header bar
  doc.setFillColor(30, 58, 95);
  doc.rect(0, 0, PAGE_WIDTH, 32, "F");

  // Logo
  if (logo) {
    try { doc.addImage(logo, "PNG", MARGIN, 4, 40, 24); } catch { /* skip logo */ }
  }

  // Title text (right of logo)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(255);
  doc.text("SDORA WELLBEING LLC", logo ? 65 : PAGE_WIDTH / 2, 14, logo ? {} : { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(200);
  doc.text("1700 Reisterstown Rd, Suite 109 | Pikesville, MD 21208", logo ? 65 : PAGE_WIDTH / 2, 20, logo ? {} : { align: "center" });
  doc.text("(877) 772-5150 | sdorawellbeing.org", logo ? 65 : PAGE_WIDTH / 2, 25, logo ? {} : { align: "center" });
  doc.setTextColor(0);

  // Report title bar
  doc.setFillColor(240, 244, 255);
  doc.rect(0, 32, PAGE_WIDTH, 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 95);
  doc.text(reportTitle, PAGE_WIDTH / 2, 39, { align: "center" });
  doc.setTextColor(0);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(subtitle, PAGE_WIDTH / 2, 46, { align: "center" });
    doc.setTextColor(0);
    return 50;
  }
  return 46;
}

// ── Watermark on pages 2+ ──
function addWatermarkAndFooters(doc) {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Footer
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`SDORA Wellbeing LLC — CONFIDENTIAL | Page ${i} of ${totalPages}`, PAGE_WIDTH / 2, 290, { align: "center" });
    doc.text("Generated: " + new Date().toLocaleString("en-US"), MARGIN, 290);
    doc.setTextColor(0);

    // Watermark on pages 2+
    if (i > 1) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(50);
      doc.setTextColor(230, 235, 245);
      doc.text("SDORA", PAGE_WIDTH / 2, PAGE_HEIGHT / 2 - 8, { align: "center" });
      doc.setFontSize(20);
      doc.text("WELLBEING LLC", PAGE_WIDTH / 2, PAGE_HEIGHT / 2 + 8, { align: "center" });
      doc.setTextColor(0);
    }
  }
}


// ═══════════════════════════════════════════════
//  REFERRAL PDF
// ═══════════════════════════════════════════════
export async function generateReferralDetailPDF(referral) {
  const logo = await loadLogo();
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const rd = referral.referral_data || {};
  const omhc = rd.omhc || {};
  const prp = rd.prp || {};
  const sigs = rd.signatures || {};
  const notes = referral.notes || [];
  const category = rd.referralCategory || "N/A";
  const clientName =
    [omhc.firstName, omhc.middleName, omhc.lastName].filter(Boolean).join(" ") ||
    [prp.firstName, prp.middleName, prp.lastName].filter(Boolean).join(" ") ||
    omhc.legalName || prp.legalName || "Unknown";

  const h = createPdfHelpers(doc);

  // Page 1 letterhead
  const startY = addLetterhead(doc, logo, "Referral Detail Report", `${clientName} | ${category} | ${referral.status || "submitted"} | ${referral.submitted_at ? new Date(referral.submitted_at).toLocaleDateString("en-US") : "N/A"}`);
  h.setY(startY + 4);

  // Referral Source
  h.addSectionHeader("Referral Source Information");
  h.addFieldRow([["Date", rd.referralDate], ["Source", rd.referralSource]]);
  h.addFieldRow([["Provider", rd.referringProvider], ["Credentials", rd.credentials]]);
  h.addFieldRow([["NPI", rd.npi], ["Phone", rd.phone]]);
  h.addFieldRow([["Fax", rd.fax], ["Email", rd.email]]);
  h.addFieldRow([["Category", category], ["Urgency", rd.urgencyLevel]]);
  h.spacer();

  // PHI
  h.addSectionHeader("Protected Health Information");
  if (referral.has_ssn_omhc) h.addField("OMHC SSN (masked)", referral.ssn_omhc_masked);
  if (referral.has_ssn_prp) h.addField("PRP SSN (masked)", referral.ssn_prp_masked);
  if (!referral.has_ssn_omhc && !referral.has_ssn_prp) h.addField("SSN", "Not on file");
  h.spacer();

  // OMHC
  if (category === "OMHC" || category === "Both") {
    h.addSectionHeader("OMHC — Client Demographics");
    h.addFieldRow([["First Name", omhc.firstName], ["Last Name", omhc.lastName]]);
    h.addFieldRow([["DOB", omhc.dob], ["Age", omhc.age]]);
    h.addFieldRow([["Sex", omhc.sexAssigned], ["Gender", omhc.genderIdentity]]);
    h.addFieldRow([["Race", omhc.race], ["Ethnicity", omhc.ethnicity]]);
    h.spacer();
    h.addSectionHeader("OMHC — Clinical");
    h.addFieldRow([["Primary Dx", omhc.primaryDiagnosis], ["Code", omhc.primaryCode]]);
    h.addFieldRow([["Secondary Dx", omhc.secondaryDiagnosis], ["Code", omhc.secondaryCode]]);
    h.addLongText("Presenting Problem", omhc.presentingProblem);
    h.spacer();
    h.addSectionHeader("OMHC — Insurance");
    h.addFieldRow([["Insurance", omhc.insuranceType], ["Medicaid ID", omhc.medicaidId]]);
    h.spacer();
  }

  // PRP
  if (category === "PRP" || category === "Both") {
    h.addSectionHeader("PRP — Client Demographics");
    h.addFieldRow([["First Name", prp.firstName], ["Last Name", prp.lastName]]);
    h.addFieldRow([["DOB", prp.dob], ["Diagnosis", prp.currentDiagnosis]]);
    h.addLongText("Reason for Referral", prp.reasonForReferral);
    h.spacer();
  }

  // Signatures
  if (Object.keys(sigs).length > 0) {
    h.addSectionHeader("Signatures");
    h.addFieldRow([["Under Supervision", sigs.underSupervision], ["Internal Status", sigs.internalStatus]]);
    h.addField("Typed Signature", sigs.typedSignature);
    h.spacer();
  }

  // Notes
  h.addNotes(notes);

  // Watermark + footers
  addWatermarkAndFooters(doc);

  const lastName = omhc.lastName || prp.lastName || "Unknown";
  const date = new Date().toISOString().split("T")[0];
  doc.save(`SDORA_${category}_Referral_${lastName}_${date}.pdf`);
}


// ═══════════════════════════════════════════════
//  INTAKE PDF
// ═══════════════════════════════════════════════
export async function generateIntakeDetailPDF(intake) {
  const logo = await loadLogo();
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const fd = intake.form_data || {};
  const p = fd.personalInfo || {};
  const ec = fd.emergencyContact || {};
  const ins = fd.insurance || {};
  const ref = fd.referralInfo || {};
  const pc = fd.presentingConcerns || {};
  const mh = fd.medicalHistory || {};
  const ss = fd.safetyScreening || {};
  const sf = fd.socialFunctional || {};
  const cons = fd.consents || {};
  const notes = intake.notes || [];

  const clientName = [p.legalFirstName, p.legalMiddleName, p.legalLastName].filter(Boolean).join(" ") || "Unknown";

  const h = createPdfHelpers(doc);

  // Page 1 letterhead
  const startY = addLetterhead(doc, logo, "Client Intake Form", `${clientName} | ${ref.requestedService || "N/A"} | ${intake.status || "new"}`);
  h.setY(startY + 4);

  if (intake.safety_flag) {
    h.checkPage(10);
    doc.setFillColor(254, 226, 226);
    doc.rect(MARGIN, h.getY(), CONTENT_WIDTH, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(185, 28, 28);
    doc.text("SAFETY FLAG — Positive safety screening responses require staff review", MARGIN + 3, h.getY() + 5.5);
    doc.setTextColor(0);
    h.setY(h.getY() + 12);
  }

  // Personal Info
  h.addSectionHeader("Personal Information");
  h.addFieldRow([["First Name", p.legalFirstName], ["Last Name", p.legalLastName]]);
  h.addFieldRow([["Middle", p.legalMiddleName], ["Preferred Name", p.preferredName]]);
  h.addFieldRow([["DOB", p.dob], ["Sex at Birth", p.sexAtBirth]]);
  h.addFieldRow([["Gender", p.genderIdentity], ["Pronouns", p.pronouns]]);
  h.addFieldRow([["Phone", p.phone], ["Email", p.email]]);
  h.addField("Address", [p.addressLine1, p.addressLine2, p.city, p.state, p.zip].filter(Boolean).join(", "));
  h.addFieldRow([["Contact Pref", p.preferredContact], ["Language", p.languagePreference]]);
  h.addFieldRow([["Marital Status", p.maritalStatus], ["Race/Ethnicity", [p.race, p.ethnicity].filter(Boolean).join(" / ")]]);
  if (intake.ssn_masked) h.addField("SSN (masked)", intake.ssn_masked);
  if (p.isMinor === "Yes") {
    h.addFieldRow([["Guardian", p.guardianName], ["Guardian Phone", p.guardianPhone]]);
    h.addField("Guardian Relationship", p.guardianRelationship);
  }
  h.spacer();

  // Emergency Contact
  h.addSectionHeader("Emergency Contact");
  h.addFieldRow([["Name", ec.ecName], ["Relationship", ec.ecRelationship]]);
  h.addFieldRow([["Phone", ec.ecPhone], ["Alt Phone", ec.ecAltPhone]]);
  h.addField("Permission to Contact", ec.ecPermissionToContact);
  h.spacer();

  // Insurance
  h.addSectionHeader("Insurance Information");
  h.addFieldRow([["Carrier", ins.insuranceCarrier], ["Member ID", ins.memberId]]);
  h.addFieldRow([["Group #", ins.groupNumber], ["Policyholder", ins.policyholderName]]);
  h.addFieldRow([["Policyholder DOB", ins.policyholderDob], ["Relationship", ins.policyholderRelationship]]);
  if (ins.hasSecondaryInsurance === "Yes") {
    h.addFieldRow([["Secondary Carrier", ins.secondaryCarrier], ["Secondary ID", ins.secondaryMemberId]]);
  }
  h.spacer();

  // Service Info
  h.addSectionHeader("Service Information");
  h.addFieldRow([["How Heard", ref.howHeard], ["Provider", ref.referringProvider]]);
  h.addField("Requested Service", ref.requestedService);
  h.addLongText("Reason for Referral", ref.reasonForReferral);
  h.spacer();

  // Presenting Concerns
  h.addSectionHeader("Presenting Concerns");
  h.addLongText("Primary Concerns", pc.primaryConcerns);
  h.addLongText("Symptoms", pc.symptoms);
  h.addFieldRow([["Onset", pc.onsetDate], ["Functioning", pc.functioningLevel]]);
  h.addLongText("Treatment Goals", pc.treatmentGoals);
  h.addLongText("Previous Treatment", pc.previousTreatment);
  h.spacer();

  // Medical History
  h.addSectionHeader("Medical / Behavioral Health History");
  h.addField("PCP", mh.primaryCareProvider);
  h.addLongText("Current Diagnoses", mh.currentDiagnoses);
  h.addLongText("Medical Conditions", mh.medicalConditions);
  h.addField("Allergies", mh.allergies);
  h.addLongText("Current Medications", mh.currentMedications);
  h.addLongText("Substance Use", mh.substanceUseHistory);
  h.spacer();

  // Safety Screening
  h.addSectionHeader("Safety Screening" + (intake.safety_flag ? " — FLAGGED" : ""));
  h.addFieldRow([["Suicidal Thoughts", ss.suicidalThoughts], ["Self-Harm", ss.selfHarm]]);
  h.addFieldRow([["Harm to Others", ss.harmToOthers], ["Recent Crisis", ss.recentCrisis]]);
  h.addField("Immediate Risk", ss.immediateSafetyRisk);
  if (ss.safetyDetails) h.addLongText("Details", ss.safetyDetails);
  h.spacer();

  // Social/Functional
  h.addSectionHeader("Social & Functional Information");
  h.addFieldRow([["Housing", sf.housingStatus], ["Employment", sf.employmentStatus]]);
  h.addFieldRow([["Transportation", sf.transportationNeeds], ["Legal", sf.legalInvolvement]]);
  h.addLongText("Support System", sf.supportSystem);
  h.spacer();

  // Consents
  h.addSectionHeader("Consents & Signature");
  if (fd.personalInfo?.isMinor === "Yes") {
    h.addField("Signing On Behalf of Minor As", cons.guardianType);
  } else if (cons.hasLegalRepresentative === "Yes") {
    h.addField("Legal Representative", `${cons.legalRepName || ""} (${cons.legalRepRelationship || ""})`);
  }
  const consentItems = [
    ["Item 1 - Treatment Consent", cons.consentToTreatment], ["Item 2 - Privacy Practices", cons.privacyPractices],
    ["Item 3 - Electronic Communication", cons.electronicCommunication], ["Item 4 - Telehealth Policy", cons.telehealthConsent],
    ["Item 5 - Emergency Contact Auth", cons.emergencyContactAuth], ["Item 6 - Financial Responsibility", cons.financialResponsibility],
    ["Item 7 - Grievance & Complaint Procedure", cons.grievanceProcedure], ["Item 8 - Client Rights & Responsibilities", cons.clientRights],
  ];
  consentItems.forEach(([lbl, val]) => {
    const agreed = val === true || val === "Yes";
    h.addField(lbl, agreed ? "Agreed" : "Not agreed");
  });
  h.addFieldRow([["Item 9 - Service Delivery Preference", cons.serviceDeliveryPreference], ["Initials", cons.serviceDeliveryPreferenceInitials]]);
  if (ref.requestedService === "PRP — Psychiatric Rehabilitation") {
    h.addFieldRow([["Item 10 - PRP Explained (Received in Writing)", cons.prpAcknowledgment ? "Agreed" : "Not agreed"], ["Initials", cons.prpAcknowledgmentInitials]]);
  } else {
    h.addField("Item 10 - PRP Acknowledgment", "Not applicable: no PRP services selected");
  }
  h.addFieldRow([["Item 11 - Transportation Authorization", cons.transportationConsent], ["Initials", cons.transportationConsentInitials]]);
  h.addField("Item 12 - Right to Refuse Treatment", cons.rightToRefuse ? "Agreed" : "Not agreed");
  if (cons.releaseOfInfoDetails) h.addLongText("Release of Info", cons.releaseOfInfoDetails);
  h.addField("Electronic Signature", cons.signatureText);
  h.addFieldRow([["Printed Name", cons.signaturePrintedName], ["Relationship", cons.signatureRelationship]]);
  h.addField("Consent Effective Date", cons.consentEffectiveDate || cons.signatureDate);
  if (cons.effectiveDateReason) h.addLongText("Reason for Earlier Effective Date", cons.effectiveDateReason);
  h.addField("Electronically Signed (actual, system-recorded)", intake.submitted_at ? new Date(intake.submitted_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "");
  h.spacer();

  // Notes
  h.addNotes(notes);

  // Watermark + footers
  addWatermarkAndFooters(doc);

  const lastName = p.legalLastName || "Unknown";
  const date = new Date().toISOString().split("T")[0];
  doc.save(`SDORA_Intake_${lastName}_${date}.pdf`);
}
